import { type PropsWithChildren, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth-provider';
import { hasExplicitLogout } from '../logout-intent';
import { sessionOwner } from '../session-boundary';
import { LegacyDraftRecoveryDialog, SessionRecoveryDialog } from './SessionRecoveryDialog';

function AuthGuardFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-[var(--color-text-tertiary)]">
      Loading...
    </div>
  );
}

export function AuthGuard({ children }: PropsWithChildren) {
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading,
    session,
    sessionExpired,
    legacyDrafts,
    resetToList,
    acknowledgeContextReset,
  } = useAuth();
  useEffect(() => {
    if (resetToList && location.pathname === '/' && !location.search && !location.hash)
      acknowledgeContextReset?.();
  }, [resetToList, location, acknowledgeContextReset]);

  if (isLoading) {
    return <AuthGuardFallback />;
  }

  if (resetToList && (location.pathname !== '/' || location.search || location.hash))
    return <Navigate to="/" replace />;

  if (!isAuthenticated && !sessionExpired) {
    const redirectTo = `${location.pathname}${location.search}${location.hash}`;
    const shouldAutoStartM365 = session.authMode === 'm365' && !hasExplicitLogout();
    return (
      <Navigate
        to={shouldAutoStartM365 ? '/login?auto=1' : '/login'}
        replace
        state={{ redirectTo }}
      />
    );
  }

  return (
    <>
      {legacyDrafts?.length ? (
        <LegacyDraftRecoveryDialog />
      ) : (
        <div key={sessionOwner(session)} inert={sessionExpired || undefined}>
          {children}
        </div>
      )}
      {sessionExpired ? <SessionRecoveryDialog /> : null}
    </>
  );
}
